import React from "react";
import Tooltip from "@mui/material/Tooltip";

import { TextEntryFormControl } from "../TextEntryFormControl";
import { TextControlStyle } from "../../Utilities/FormDesignConstants";
import { StyledFormHelperText, StyledTextField } from "./CommonStyled";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

class XTimePickerEditUI extends React.Component<{ editMe: TimePickerFormControl }> {
    render() {
        return (
            <UISpacedBoxContent>
                {this.props.editMe.renderBaseDataControlEditor()}
                {this.props.editMe.renderTextControlBasePropertyEditor()}
                {this.props.editMe.renderTextStyleSelectionEditor()}
            </UISpacedBoxContent>
        );
    }
}

export class TimePickerFormControl extends TextEntryFormControl {
    readonly __type: string = "Time Picker";

    render() {
        let style = this.getFormContext()?.form?.defaultTextStyle;

        if (this.overrideStyle && this.style) style = this.style;

        let size: "medium" | "small" = this.getFormContext()?.form?.defaultTextSize ?? "medium";

        if (this.overrideStyle && this.size) size = this.size;

        const issueData: IssueData | null = this.getFormContext()!.getControlContext(this)!.getRuntimeIssueData();
        const issueText: string = issueData?.text || "";
        const customWidth = this.fullWidth ? "100%" : this.width ? `${this.width}px` : "200px";

        switch (style) {
            case TextControlStyle.LABELED:
                return (
                    <>
                        {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                        <StyledTextField
                            size={size}
                            data-size={size}
                            variant={"outlined"}
                            fullWidth={this.fullWidth}
                            type="time"
                            value={this.valueName ? this.getFormDataValue(this.valueName) : String}
                            onChange={this.handleChange}
                            data-testid={this.valueName}
                            width={customWidth}
                            error={Boolean(issueText)}
                            InputProps={{
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "timepicker"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "timepicker"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </>
                );
            case TextControlStyle.SHADED:
                return (
                    <>
                        <StyledTextField
                            size={size}
                            data-size={size}
                            variant={"filled"}
                            customstyle={"filled"}
                            fullWidth={this.fullWidth}
                            type="time"
                            value={this.valueName ? this.getFormDataValue(this.valueName) : String}
                            onChange={this.handleChange}
                            data-testid={this.valueName}
                            width={customWidth}
                            error={Boolean(issueText)}
                            label={this.valueName}
                            InputProps={{
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "timepicker"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "timepicker"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </>
                );
            case TextControlStyle.UNDERLINED:
                return (
                    <>
                        <StyledTextField
                            size={size}
                            data-size={size}
                            variant={"standard"}
                            customstyle={"standard"}
                            fullWidth={this.fullWidth}
                            type="time"
                            value={this.valueName ? this.getFormDataValue(this.valueName) : String}
                            onChange={this.handleChange}
                            data-testid={this.valueName}
                            width={customWidth}
                            error={Boolean(issueText)}
                            label={this.valueName}
                            ispicker={"true"}
                            InputProps={{
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "timepicker"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "timepicker"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </>
                );
            case TextControlStyle.OUTLINE:
                return (
                    <>
                        <StyledTextField
                            size={size}
                            data-size={size}
                            variant={"outlined"}
                            customstyle={"outlined"}
                            fullWidth={this.fullWidth}
                            type="time"
                            value={this.valueName ? this.getFormDataValue(this.valueName) : String}
                            onChange={this.handleChange}
                            data-testid={this.valueName}
                            width={customWidth}
                            error={Boolean(issueText)}
                            label={this.valueName}
                            ispicker={"true"}
                            InputProps={{
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "timepicker"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "timepicker"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </>
                );
        }
    }

    renderEditUI(): JSX.Element | null {
        return <XTimePickerEditUI editMe={this} />;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new StringDataDefinition();
        def.name = this.valueName;
        return def;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "__type");

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "__type");
    }

    private handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (this.valueName) {
            this.setFormDataValue(this.valueName, event.currentTarget.value);
        }
    };
}
